import type { CatalogCoverage, MacroPolarity, RunHypothesis, Status, Subject } from "./contracts";
export { formatRunDuration as formatDuration, runDurationLabel, runAbortLabel } from "@calega/exercise-engine";

export const statusLabels: Record<Status, string> = {
  insufficient_data: "Zu wenig Daten",
  stable: "Unauffällig",
  suspected: "Verdacht",
  manifest: "Auffällig / manifest",
  not_observed: "Noch nicht beobachtet",
  emerging: "Im Aufbau",
  established: "Gefestigt",
};

export const coverageLabels: Record<CatalogCoverage, string> = {
  complete: "Vollständig",
  partial: "Teilweise",
  none: "Keine Analyse",
};

export const runStatusLabels: Record<string, string> = {
  STARTED: "Begonnen",
  COMPLETED: "Abgeschlossen",
  ABORTED: "Abgebrochen",
  LEGACY: "Importiert",
};

export const periodOptions = [
  { value: "30", label: "Letzte 30 Tage" },
  { value: "90", label: "Letzte 3 Monate" },
  { value: "365", label: "Letztes Jahr" },
  { value: "", label: "Gesamter Zeitraum" },
];

export const concernStatusOrder: Status[] = ["manifest", "suspected", "stable", "insufficient_data"];
export const strengthStatusOrder: Status[] = ["established", "emerging", "not_observed", "insufficient_data"];
export const learningPhaseLabels: Record<string, string> = {
  move_tens: "Zehner bewegen",
  observe_material: "Material anschauen",
  mental_short: "Kurz im Kopf",
};

export function formatExerciseType(value: string) {
  return value.replace(/[-_]+/g, " ").replace(/^./, (letter) => letter.toLocaleUpperCase("de-DE"));
}

const analysisDetailLabels: Record<string, string> = {
  sentence_start: "Satzanfang",
  standard_noun: "gewöhnliches Nomen",
  signal_nominalization: "Nominalisierung mit Signalwort",
  attributive_adjective: "attributives Adjektiv",
  predicate_verb: "Prädikatsverb",
  proper_name: "Eigenname",
  time_quantity: "Zeit- oder Mengenangabe",
  other: "weiterer Satzkontext",
  standard: "Standardfall",
  capitalized_distractor: "großgeschriebener Ablenker",
  nominalized: "Nominalisierung",
  adverbial_use: "adverbialer Gebrauch",
  confirmed_selection: "sicher bestätigt",
  lucky_guess_overgeneralization: "richtiges Graphem ohne passende Ableitung",
  phonetic_surface_bias: "an der Lautoberfläche orientiert",
  visual_homophone_error: "unpassende formale Ableitung",
  syllable_trap: "nach Sprechsilben getrennt",
  prefix_stem_blend: "Präfix und Stamm verschmolzen",
  suffix_stem_blend: "Stamm und Suffix verschmolzen",
  prefix_slot_omitted: "Präfix ausgelassen",
  suffix_slot_omitted: "Suffix ausgelassen",
  invented_prefix: "Präfix erfunden",
  invented_suffix: "Suffix erfunden",
  boundary_error: "Bausteingrenze verschoben",
};

export function analysisDetailLabel(value: string) {
  return analysisDetailLabels[value] ?? "Fachlicher Kontext";
}

function formatPercentage(value: number, total: number | null) {
  return total !== null && total > 0 ? ` (${Math.round((value / total) * 100)}%)` : "";
}

export function formatRunScore(score: number | null, maxScore: number | null) {
  return `${score ?? "–"}/${maxScore ?? "–"} richtig${score === null ? "" : formatPercentage(score, maxScore)}`;
}

export function formatWrongAnswers(wrongAnswerCount: number, maxScore: number | null) {
  return `${wrongAnswerCount} falsch${formatPercentage(wrongAnswerCount, maxScore)}`;
}

export function periodLabel(days: number | null) {
  return periodOptions.find((option) => (option.value ? Number(option.value) : null) === days)?.label ?? "Gesamter Zeitraum";
}

export function hypothesisOriginLabel(origin: RunHypothesis["origin"]) {
  if (origin === "legacy_v1_translated") return "Aus V1 übersetzt";
  if (origin === "legacy_v1_recomputed") return "Aus V1-Fachdaten neu berechnet";
  if (origin === "legacy_v1_preserved") return "Originaler V1-Hinweis";
  return "Calepia-Analyse";
}

export function evidenceCountLabel(count: number) {
  return `${count} ${count === 1 ? "Beleg" : "Belege"}`;
}

export function partitionRunHypotheses<T extends { status: RunHypothesis["status"] }>(hypotheses: T[]) {
  return {
    active: hypotheses.filter(({ status }) => status === "triggered"),
    other: hypotheses.filter(({ status }) => status !== "triggered"),
  };
}

export function filterRunItems<T extends { key: string; correct: boolean | null }>(items: T[], itemKeys: string[] | null, hideCorrect: boolean) {
  const keys = itemKeys ? new Set(itemKeys) : null;
  return items.filter((item) => (!keys || keys.has(item.key)) && (!hideCorrect || item.correct !== true));
}

export function matchingRunItemKeys<T extends { key: string }>(items: T[], itemKeys: string[]) {
  const documentedKeys = new Set(items.map(({ key }) => key));
  return [...new Set(itemKeys.filter((itemKey) => documentedKeys.has(itemKey)))];
}

export function filterCatalogEntries<T extends { subject: Subject; coverage: CatalogCoverage }>(
  entries: T[],
  subject: Subject | "all",
  coverage: CatalogCoverage | "all",
) {
  return entries.filter((entry) => (subject === "all" || entry.subject === subject) && (coverage === "all" || entry.coverage === coverage));
}

export function isAlertCluster(cluster: { polarity: MacroPolarity; status: Status }) {
  return cluster.polarity === "concern" && (cluster.status === "manifest" || cluster.status === "suspected");
}
